import React, { useState, useRef } from 'react';
import { checkValue, localization, formatMonth, dateHelper, getUniqueListBy } from 'src/components/common/helper';
import { useBexApi } from '@shared-ui/bex-api-context';
// Module Context
import { useComponentContext } from 'src/components/common/modules/component-layout/component-context';

// Components
import { useWindowDimensions } from 'src/components/common/useWindowDimentions';
import DealFixturesView from 'src/components/common/modules/v1/deal-fixtures/components/deal-fixtures-view';

// Css Style Import
import(/* webpackChunkName: "v1-deal-fixtures-styles" */ 'src/components/common/modules/v1/deal-fixtures/styles/deal-fixtures.css');

export const MONTH_FILTER_KEY = 'eventMonthId';
export const TEAM_FILTER_KEY = 'teamName';

const initialFilter = {
  [MONTH_FILTER_KEY]: '',
  [TEAM_FILTER_KEY]: '',
};

const DealFixtures: React.FC = () => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps: { data, key, id, mccidData, ...rest },
  } = useComponentContext();
  const {
    context: { locale },
  } = useBexApi();
  const { fixtureList } = data;

  const isFutureEvent = (expDate: string) => {
    if (!checkValue(expDate)) return true;
    return dateHelper.isDateInFuture(new Date(expDate));
  };

  const dealsList = fixtureList
    .map((el) => {
      const monthId = localization.formatDateString(el?.eventDateTime, { skeleton: 'M' });
      const eventMonthId = checkValue(monthId) && monthId !== 'Invalid date' ? monthId : '';
      const teams = el?.title?.split(' vs ');
      if (el?.isStaticTile === "True") {
        return {
          ...el,
          teamName: '',
          eventMonthId: ''
        };
      } else if (isFutureEvent(el?.eventExpiryDate)) {
        if (checkValue(teams, true)) {
          return teams.map((teamName: string) => {
            return {
              ...el,
              teamName: teamName.trim(),
              eventMonthId,
            };
          });
        } else {
          return {
            ...el,
            teamName: el?.title,
            eventMonthId,
          };
        }
      }
    })
    .filter((el) => checkValue(el))
    .flat();

  const formatOptions = (optionList, key: string, isNumber: boolean) => {
    if (!checkValue(optionList, true)) return [];
    const uniqueOptions = [...new Set(optionList.map((el) => el[key]))].filter((el) => checkValue(el));
    if (isNumber) return uniqueOptions.sort((a: number, b: number) => a - b);
    return uniqueOptions.sort((a: string, b: string) => a.localeCompare(b));
  };

  const setOptions = (optionList, filterTypeName: string) => {
    const options = formatOptions(optionList, filterTypeName, filterTypeName === MONTH_FILTER_KEY);
    if (!checkValue(options, true)) return [];
    if (filterTypeName === MONTH_FILTER_KEY) {
      return options.map((el: any) => ({
        optionLabel: formatMonth(el - 1, { skeleton: 'MMMM', locale }),
        optionValue: el
      }));
    } else {
      return options.map((el: any) => ({
        optionLabel: el,
        optionValue: el
      }));
    }
  };

  const { width: windowWidth } = useWindowDimensions();
  let initialSlideCount = 0;
  if (windowWidth > 992) initialSlideCount = 3;
  else if (windowWidth > 575) initialSlideCount = 2;
  else initialSlideCount = 1;

  const [dealCardList, setDealCardList] = useState(dealsList);
  const [filters, setFilters] = useState(initialFilter);
  const [resetSearchResults, setResetSearchResults] = useState(0);
  const [monthFilterOptions, setMonthFilterOptions] = useState(setOptions(dealsList, MONTH_FILTER_KEY));
  const [teamFilterOptions, setTeamFilterOptions] = useState(setOptions(dealsList, TEAM_FILTER_KEY));
  const sliderRef = useRef(null);

  const handleFilter = (e, type: string) => {
    const { value } = e.target;
    const filterValues = {
      ...filters,
      [type]: value,
    };
    let filteredList = dealsList;
    Object.keys(filterValues).forEach((filterKey) => {
      if (checkValue(filterValues[filterKey]))
        filteredList = filteredList.filter((el) => el[filterKey] === filterValues[filterKey]);
    });
    filterDeals(type, filteredList, filterValues);
  };

  const filterDeals = (type: string, filteredList: any, filterValues: any) => {
    const dependentType = type === MONTH_FILTER_KEY ? TEAM_FILTER_KEY : MONTH_FILTER_KEY;
    const dependentList = checkValue(filterValues[type])
      ? dealsList.filter((element) => element[type] === filterValues[type])
      : dealsList;
    if (dependentType === TEAM_FILTER_KEY) setTeamFilterOptions(setOptions(dependentList, dependentType));
    else if (dependentType === MONTH_FILTER_KEY) setMonthFilterOptions(setOptions(dependentList, dependentType));
    setResetSearchResults(resetSearchResults + 1);
    setFilters(filterValues);
    setDealCardList(filteredList);
  };

  const fixtureProps = {
    ...data,
    dealCardList: getUniqueListBy(dealCardList, 'uniquekey'),
    fixtureList,
    resetSearchResults,
    mccidData,
    id,
    key,
    filters,
    handleFilter,
    initialSlideCount,
    sliderRef,
    monthFilterOptions,
    teamFilterOptions,
  };

  return checkValue(dealsList, true) ? <DealFixturesView {...fixtureProps} /> : null;
};

export default DealFixtures;
